import React, { useState, useEffect } from 'react';
import { Offcanvas, Table, Card, Spinner, Alert, Badge, Row, Col, Button, Collapse, Tabs, Tab, Modal } from 'react-bootstrap';
import axios from 'axios';
import dayjs from 'dayjs';
import { FiInfo, FiFileText, FiHash, FiCalendar, FiExternalLink, FiBriefcase, FiChevronDown, FiChevronRight, FiCheckCircle, FiEye, FiDownload, FiPrinter } from 'react-icons/fi';
import ExpenseModal from '../project/expense/ExpenseModal';
import TaxInvoicePreview from '../ProformaInvoice/TaxInvoicePreview';
import { saveAs } from 'file-saver';
import Swal from 'sweetalert2';

const LedgerDetailsOffcanvas = ({ show, onHide, ledger, startDate, endDate, projectId }) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [invoices, setInvoices] = useState([]);
    const [expandedInvoices, setExpandedInvoices] = useState(new Set());
    const [entryDetails, setEntryDetails] = useState({});
    const [fetchingEntry, setFetchingEntry] = useState(null);
    const [webzashStatement, setWebzashStatement] = useState(null);
    const [loadingStatement, setLoadingStatement] = useState(false);
    const [calculatedBalance, setCalculatedBalance] = useState(null);
    const [activeTab, setActiveTab] = useState('summary');
    const [resolvedLedger, setResolvedLedger] = useState(null);
    const [pendingOnly, setPendingOnly] = useState(false);
    const [isLiabilityLedger, setIsLiabilityLedger] = useState(false);

    // Detail States
    const [showExpenseModal, setShowExpenseModal] = useState(false);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);
    const [selectedExpenseData, setSelectedExpenseData] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [selectedQuotation, setSelectedQuotation] = useState(null);
    const [customIntegration, setCustomIntegration] = useState(false);
    const [customIntegrationDescription, setCustomIntegrationDescription] = useState('');
    const [editableDeploymentIntegration, setEditableDeploymentIntegration] = useState({});

    const [showDocOffcanvas, setShowDocOffcanvas] = useState(false);
    const [docUrl, setDocUrl] = useState('');
    const [downloadUrl, setDownloadUrl] = useState('');
    const [docTitle, setDocTitle] = useState('');

    const [users, setUsers] = useState([]);
    const [expenseTypes, setExpenseTypes] = useState([]);

    const API_BASE_URL = process.env.REACT_APP_WEBZASH_URL;
    const API_KEY = process.env.REACT_APP_API_KEY;
    const API_ORIGIN = process.env.REACT_APP_BACKEND_URL;

    useEffect(() => {
        if (show && ledger) {
            setResolvedLedger(ledger);
            const lType = determineLedgerType(ledger);

            const isLiabilityType = ['liability', 'payroll', 'vat_payable', 'employee', 'advance', 'vendor', 'sundry_creditors', 'fixed_asset'].includes(lType);
            const isReceivableType = ['client', 'sundry_debtors', 'income'].includes(lType);

            const isSpecialLedger = (isLiabilityType || isReceivableType) && !startDate;

            setPendingOnly(!!isSpecialLedger);
            setIsLiabilityLedger(isLiabilityType || isReceivableType);

            fetchData(isLiabilityType && !startDate);
        }
    }, [show, ledger, startDate, endDate, projectId]);

    const fetchData = async (forcePendingOnly = null) => {
        setLoading(true);
        setError(null);
        setInvoices([]);
        setExpandedInvoices(new Set());
        setEntryDetails({});
        setWebzashStatement(null);
        setActiveTab('summary');
        // Reset calculated balance on fetch start
        setCalculatedBalance(null);

        try {
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const headers = { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin };

            // Clean ledger name
            const cleanLedgerName = ledger.name.replace(/\s*\(.*?\)\s*$/, '').trim();
            const ledgerType = determineLedgerType(ledger);

            // 1. Resolve Ledger Metadata from Webzash first if ID is missing or to get groups
            let enrichedLedger = ledger;
            try {
                const metaResponse = await axios.get(`${API_BASE_URL}/api/ledgers/details`, {
                    headers: { 'x-api-key': API_KEY, 'Accept': 'application/json' },
                    params: { ledger_name: cleanLedgerName, ledger_type: ledgerType }
                });
                if (metaResponse.data?.status === 'success' && metaResponse.data.data) {
                    enrichedLedger = { ...ledger, ...metaResponse.data.data };
                    // If the backend returns a different name (official name), we might want to use it
                    // but usually, we stick to what the ERP/Report shows for UI consistency
                    setResolvedLedger(enrichedLedger);
                }
            } catch (metaErr) {
                console.warn('[Drilldown] Could not resolve Webzash metadata:', metaErr.message);
            }

            // 2. Fetch Webzash Statement (Pass enriched ledger)
            fetchWebzashStatement(enrichedLedger);

            // 3. Fetch Local Data
            const params = new URLSearchParams();
            const isPendingRequest = forcePendingOnly !== null ? forcePendingOnly : pendingOnly;

            // Determine ledger category
            const isAdvanceLedger = ledgerType === 'advance' || cleanLedgerName.toLowerCase().includes('advance');

            const isLiabilityLedger = !isAdvanceLedger && (['vendor', 'sundry_creditors', 'employee', 'user', 'liability', 'fixed_asset', 'payroll', 'vat_payable', 'expense'].includes(ledgerType) ||
                cleanLedgerName.toLowerCase().includes('creditor') ||
                cleanLedgerName.toLowerCase().includes('payable') ||
                cleanLedgerName.toLowerCase().includes('loan') ||
                cleanLedgerName.toLowerCase().includes('salary') ||
                cleanLedgerName.toLowerCase().includes('payroll') ||
                cleanLedgerName.toLowerCase().includes('vat') ||
                cleanLedgerName.toLowerCase().includes('fixed asset') ||
                cleanLedgerName.toLowerCase().includes('furniture') ||
                cleanLedgerName.toLowerCase().includes('equipment') ||
                cleanLedgerName.toLowerCase().includes('expense') ||
                cleanLedgerName.toLowerCase().includes('electronics'));

            const isReceivableLedger = !isAdvanceLedger && (['client', 'sundry_debtors', 'income'].includes(ledgerType) ||
                cleanLedgerName.toLowerCase().includes('debtor') ||
                cleanLedgerName.toLowerCase().includes('receivable') ||
                cleanLedgerName.toLowerCase().includes('account receivable') ||
                cleanLedgerName.toLowerCase().includes('sales'));



            if (isLiabilityLedger) {
                // ============================================================
                // LIABILITY LEDGERS: Hybrid Routing
                // ============================================================
                // Vendors/Employees -> Use STRICT Expense API (Reliable Vendor/User Name Search + Native is_paid support)
                // Assets/Expenses -> Use GENERIC Transaction API (Reliable Ledger Name/Category Search + Show All History)

                const useExpenseApi = ['vendor', 'sundry_creditors', 'employee', 'payroll', 'vat_payable', 'fixed_asset', 'expense'].includes(ledgerType) ||
                    cleanLedgerName.toLowerCase().includes('payable') ||
                    cleanLedgerName.toLowerCase().includes('fixed asset') ||
                    cleanLedgerName.toLowerCase().includes('expense');
                let apiEndpoint;

                if (useExpenseApi) {
                    // 1. EXPENSE API PATH (Requested for Fixed Assets too)
                    console.log(`[Drilldown] Fetching EXPENSES (Payable/Asset Path) for: "${cleanLedgerName}"`);
                    apiEndpoint = `${API_ORIGIN}/api/expense`;

                    // Helper: Extract Entity Name
                    let entityName = cleanLedgerName;
                    if (cleanLedgerName.includes('->')) entityName = cleanLedgerName.split('->').pop().trim();
                    else if (cleanLedgerName.includes(' - ')) entityName = cleanLedgerName.split(' - ')[1].trim();

                    // Extra cleanup for Fixed Assets if simple split failed or wasn't triggered
                    if ((ledgerType === 'fixed_asset' || cleanLedgerName.toLowerCase().includes('fixed asset')) && entityName.toLowerCase().includes('fixed asset')) {
                        // Attempt to strip prefix if still present
                        const parts = entityName.split(/-\s*/);
                        if (parts.length > 1) entityName = parts[parts.length - 1].trim();
                    }

                    // Don't filter group ledgers by name (too restrictive)
                    const isGroupLedger = ledger.is_group !== undefined ? ledger.is_group : (!cleanLedgerName.includes('->') && !cleanLedgerName.includes(' - '));
                    if (!isGroupLedger || (ledgerType !== 'payroll' && ledgerType !== 'vat_payable')) {
                        params.append('search', entityName);
                        // If it's NOT a group, we want specific results. Enable exact match on backend.
                        if (!isGroupLedger) {
                            params.append('exact_match', '1');
                            params.append('search_context', 'ledger');
                        } else {
                            params.append('search_context', 'group');
                        }
                    }

                    // Unpaid Filter: Apply ONLY for Vendors/Payables.
                    // EXCLUDE Fixed Assets (we want full history accumulation).
                    const isStrictPayable = !cleanLedgerName.toLowerCase().includes('fixed asset') && ledgerType !== 'fixed_asset';
                    if (isPendingRequest && isStrictPayable) params.append('is_paid', 'false');

                    // VAT Special Handling
                    if (ledgerType === 'vat_payable' || cleanLedgerName.toLowerCase().includes('vat payable')) {
                        apiEndpoint = `${API_ORIGIN}/api/vat-payments`;
                        params.append('status', 'submitted');
                        if (isPendingRequest) {
                            params.delete('is_paid'); // Remove if already added by general logic
                            params.append('is_paid', 'false');
                        }
                    } else if (ledgerType === 'payroll' || cleanLedgerName.toLowerCase().includes('salary')) {
                        params.append('type', 'salary');
                    }

                } else {
                    // 2. GENERIC API PATH (For Fixed Assets / General Expenses)
                    // Expense API search fails for these if "Expense Type" doesn't match Ledger Name exactly.
                    // Generic API matches Ledger Name robustly via TransactionController.
                    console.log(`[Drilldown] Fetching TRANSACTIONS (Generic Path) for: "${cleanLedgerName}"`);
                    apiEndpoint = `${API_ORIGIN}/api/transactions`;
                    params.append('ledger_name', cleanLedgerName);
                    params.append('ledger_type', ledgerType);

                    // For Assets/Expenses, we usually show FULL HISTORY (No Pending Filter).
                    // But if user forces pending, Generic API supports 'pending_only'.
                    // For now, defaulting to Show All (Standard Ledger View).
                    // if (isPendingRequest) params.append('pending_only', '1');
                }

                // Date Logic (Applied to Both Paths as needed)
                const lType = determineLedgerType(ledger);
                const isNominal = lType === 'income' || (lType === 'expense' && !cleanLedgerName.toLowerCase().includes('payable'));

                // For Nominal accounts (P&L), we want a PERIOD view (Include startDate).
                // For Real/Personal accounts (BS), we want a CUMULATIVE view (Exclude startDate to show balance "As Of").

                // USER REQUEST: Always pass start date param for expenses regardless of ledger type
                if (startDate) {
                    params.append('from_date', startDate);
                    params.append('start_date', startDate);
                    params.append('date_from', startDate);
                    params.append('from', startDate);
                }

                if (endDate) {
                    params.append('to_date', endDate);
                    params.append('end_date', endDate);
                    params.append('date_to', endDate);
                    params.append('to', endDate);
                }
                if (projectId) params.append('project_id', projectId);

                const response = await axios.get(`${apiEndpoint}?${params.toString()}`, { headers });

                // vat-payments returns paginated object, expenses also returns data.data.data or data.data
                let fetchedItems = [];
                if (response.data.data?.data) {
                    fetchedItems = response.data.data.data;
                } else if (response.data.data) {
                    fetchedItems = response.data.data;
                } else if (response.data.items) {
                    fetchedItems = response.data.items;
                } else {
                    fetchedItems = response.data;
                }

                const expenses = Array.isArray(fetchedItems) ? fetchedItems.filter(item => item.type !== 'bank_transfer') : [];

                // FALLBACK: If local Expense API returns nothing for an expense ledger, 
                // it might be a direct journal entry in Webzash. Populate from statement.
                if (expenses.length === 0 && (ledgerType === 'expense' || cleanLedgerName.toLowerCase().includes('expense'))) {
                    console.log(`[Drilldown] No local expenses found for "${cleanLedgerName}". Falling back to Webzash statement.`);
                    await fetchWebzashStatement(enrichedLedger, 'general');
                    setLoading(false);
                    return;
                }

                console.log(`[Drilldown] Found ${expenses.length} expenses`);

                const grouped = {};
                expenses.forEach(exp => {
                    const id = exp.id || 'VAT-' + (exp.fta_reference || exp.entry_id);
                    // Cascade search for the net amount portion to ensure it matches specific ledger shares
                    // Prioritize amount_without_tax (net cost) if non-zero, else fallback to standard amount or total
                    const aTax = parseFloat(exp.amount_without_tax);
                    const aVat = parseFloat(exp.amount_without_vat);
                    const aNet = parseFloat(exp.amount);
                    const aTotal = parseFloat(exp.total_amount);

                    const netAmt = (aTax > 0) ? aTax : (aVat > 0 ? aVat : (aNet > 0 ? aNet : (aTotal || 0)));
                    const grossAmt = aTotal || parseFloat(exp.total_with_vat) || netAmt;

                    const displayAmt = isPendingRequest ? (exp.pending_balance ?? grossAmt) : netAmt;

                    grouped[id] = {
                        id: id,
                        ref_no: exp.invoice_no || exp.bill_no || exp.external_id || (exp.id ? `E-${exp.id}` : ' '),
                        project_name: exp.project?.name || exp.project_name,
                        date: exp.date || exp.period_start || exp.due_date,
                        transactions: [{
                            ...exp,
                            type: exp.type || (exp.vat_type ? 'vat_payment' : 'expense'),
                            expense_id: exp.id,
                            expense_amount: grossAmt, // Keep as Gross for the "Total:" label
                            amount: netAmt,           // Net Amount
                            total_amount: grossAmt,
                            pending_balance: exp.pending_balance ?? grossAmt,
                            journal_id: exp.journal_id || exp.entry_id,
                            expense: exp
                        }],
                        status: exp.is_paid ? 'paid' : 'pending'
                    };
                    grouped[id].display_amount = displayAmt;
                });

                setInvoices(Object.values(grouped).sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));

            } else if (ledgerType === 'sales_return') {
                // ============================================================
                // SALES RETURN: Fetch CREDIT NOTES
                // ============================================================
                console.log(`[Drilldown] Fetching CREDIT NOTES for: "${cleanLedgerName}"`);

                if (endDate) {
                    params.append('to_date', endDate);
                    params.append('end_date', endDate);
                    params.append('date_to', endDate);
                    params.append('to', endDate);
                }
                if (projectId) params.append('project_id', projectId);
                params.append('search', cleanLedgerName);

                const response = await axios.get(`${API_ORIGIN}/api/credit-notes?${params.toString()}`, { headers });

                // Credit Notes returned in response.data.data.data (paginated)
                const cnList = response.data.data?.data || response.data.data || [];

                const grouped = {};
                cnList.forEach(cn => {
                    const grossAmt = parseFloat(cn.total_amount) || 0;
                    const vatAmt = parseFloat(cn.vat_amount) || 0;
                    // Prioritize basic_amount if available, otherwise calculate net from total and vat
                    const netAmt = (parseFloat(cn.basic_amount) > 0)
                        ? parseFloat(cn.basic_amount)
                        : (grossAmt - vatAmt);

                    grouped[cn.id] = {
                        id: cn.id,
                        ref_no: cn.ref_no,
                        project_name: cn.project_name || cn.tax_invoice?.proforma_invoice?.quotation?.project?.name || cn.proforma_invoice?.quotation?.project?.name || 'N/A',
                        date: cn.date,
                        transactions: [{
                            ...cn,
                            type: 'income_reversal',
                            invoice_id: cn.id,
                            amount: netAmt,
                            total_amount: grossAmt,
                            journal_id: cn.journal_id,
                            invoice: cn
                        }],
                        status: cn.status
                    };
                    grouped[cn.id].display_amount = netAmt; // Show net return for P&L alignment
                });

                setInvoices(Object.values(grouped).sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));

            } else if (isReceivableLedger || ledgerType === 'income' || ledgerType === 'sales_income' || ledgerType === 'client') {
                // ============================================================
                // RECEIVABLE LEDGERS: Fetch INVOICES (Clients, Debtors)
                // ============================================================
                console.log(`[Drilldown] Fetching INVOICES for receivable ledger: "${cleanLedgerName}"`);

                const clientName = cleanLedgerName.includes(' - ')
                    ? cleanLedgerName.split(' - ')[1]
                    : cleanLedgerName;

                const lTypeRec = determineLedgerType(ledger);
                const isNominalRec = lTypeRec === 'income' || (lTypeRec === 'expense' && !cleanLedgerName.toLowerCase().includes('payable'));

                params.append('search', clientName);

                // User Requirement: 
                // 1. Accounts Receivable (BS) -> Show UNPAID only (Outstanding Reconciliation)
                // 2. Sales Income (P&L) -> Show ALL (Revenue Reconciliation)
                if (cleanLedgerName.toLowerCase().includes('receivable') && !isNominalRec) {
                    params.append('status', 'unpaid');
                } else if (isPendingRequest) {
                    params.append('status', 'unpaid');
                }

                // For Nominal accounts (P&L), we want a PERIOD view (Include startDate).
                // For Real/Personal accounts (BS), we want a CUMULATIVE view (Exclude startDate to show balance "As Of").

                // USER REQUEST: Always pass start date param for invoices regardless of ledger type
                if (startDate) {
                    params.append('from_date', startDate);
                    params.append('start_date', startDate);
                    params.append('date_from', startDate);
                    params.append('from', startDate);
                }

                if (endDate) {
                    params.append('to_date', endDate);
                    params.append('end_date', endDate);
                    params.append('date_to', endDate);
                    params.append('to', endDate);
                }
                if (projectId) params.append('project_id', projectId);

                const response = await axios.get(`${API_ORIGIN}/api/invoices?${params.toString()}`, { headers });
                const invoices = response.data.data?.data || response.data.data || [];

                console.log(`[Drilldown] Found ${invoices.length} invoices`);

                const grouped = {};
                invoices.forEach(inv => {
                    const grossAmt = parseFloat(inv.total_amount) || parseFloat(inv.amount) || 0;
                    const vatAmt = parseFloat(inv.vat_amount) || 0;
                    const netAmt = grossAmt - vatAmt;
                    const paidAmount = parseFloat(inv.total_paid) || 0;
                    const balanceAmount = parseFloat(inv.balance_amount) || (grossAmt - paidAmount);

                    grouped[inv.id] = {
                        id: inv.id,
                        ref_no: inv.ref_no || `INV-${inv.id}`,
                        project_name: inv.project_name || inv.quotation?.project?.name || inv.project?.name || '',
                        date: inv.date || inv.created_at,
                        transactions: [{
                            ...inv,
                            type: 'income',
                            invoice_id: inv.id,
                            amount: netAmt,
                            total_amount: grossAmt,
                            paid_amount: paidAmount,
                            pending_balance: balanceAmount,
                            journal_id: inv.journal_id || inv.tax_invoices?.[0]?.journal_id,
                            invoice: inv
                        }],
                        status: inv.invoice_status || inv.pi_status || inv.status
                    };
                    grouped[inv.id].display_amount = isPendingRequest ? balanceAmount : netAmt;
                });

                setInvoices(Object.values(grouped).sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));
            } else if (isAdvanceLedger || ledgerType === 'bank' || ledgerType === 'cash') {
                // ============================================================
                // ADVANCE/BANK/CASH LEDGERS: Use Webzash Statement Data
                // ============================================================
                console.log(`[Drilldown] Using Webzash Statement for: "${cleanLedgerName}" (${ledgerType})`);
                await fetchWebzashStatement(enrichedLedger, isAdvanceLedger ? 'advance' : 'general');
                setLoading(false);

            } else {
                // ============================================================
                // OTHER LEDGERS: Fetch TRANSACTIONS (Banks, Capital, Advances, etc.)
                // ============================================================
                params.append('ledger_name', cleanLedgerName);
                params.append('ledger_type', ledgerType);

                if (isPendingRequest || isAdvanceLedger) params.append('pending_only', '1');

                // For Balance Sheet items (Assets/Liabilities) and potentially expenses viewed historically,
                // users often want "Up To Date" rather than "In Period".
                // If the user wants "Up To", we should skip startDate.
                // However, be careful not to load too much history by default.
                // The user specifically asked: "balance sheet shown as upto 2020 dec 31... want same upto 2020 dec upto"
                // This implies ignoring start date for these drilldowns.

                const lType = determineLedgerType(ledger);
                const isNominal = lType === 'income' || (lType === 'expense' && !cleanLedgerName.toLowerCase().includes('payable'));

                if (startDate && isNominal) {
                    params.append('from_date', startDate);
                    params.append('start_date', startDate);
                    params.append('date_from', startDate);
                    params.append('from', startDate);
                }
                if (endDate) {
                    params.append('to_date', endDate);
                    params.append('end_date', endDate);
                    params.append('date_to', endDate);
                    params.append('to', endDate);
                }
                if (projectId) params.append('project_id', projectId);

                console.log(`[Drilldown] Fetching transactions for ledger: "${cleanLedgerName}" (Type: ${ledgerType})`);
                const response = await axios.get(`${API_ORIGIN}/api/transactions?${params.toString()}`, { headers });
                const transactions = response.data.data || [];

                // FALLBACK: If local Transaction API returns nothing for Bank/Cash or general ledgers,
                // it might be a direct entry (Journal/Receipt/Payment) in Webzash. Populate from statement.
                if (transactions.length === 0 && (ledgerType === 'bank' || ledgerType === 'cash' || ledgerType === 'other' || ledgerType === 'capital')) {
                    console.log(`[Drilldown] No local transactions found for "${cleanLedgerName}" (${ledgerType}). Falling back to Webzash statement.`);
                    await fetchWebzashStatement(enrichedLedger, 'general');
                    setLoading(false);
                    return;
                }

                console.log(`[Drilldown] Found ${transactions.length} transactions`);

                const grouped = {};
                transactions.forEach(trx => {
                    const invId = trx.invoice_id || 'Misc-' + (trx.external_id || trx.id);
                    if (!grouped[invId]) {
                        grouped[invId] = {
                            id: invId,
                            ref_no: trx.invoice?.ref_no || trx.bill_no || trx.reference_no || ' ',
                            project_name: trx.project?.name || trx.invoice?.quotation?.project?.name || '',
                            date: trx.date,
                            transactions: [],
                            status: trx.invoice?.pi_status || (trx.is_paid ? 'paid' : 'pending')
                        };
                    }
                    grouped[invId].transactions.push(trx);

                    // Robust field resolution for generic transactions
                    const aNet = parseFloat(trx.amount);
                    const iTax = parseFloat(trx.invoice?.amount_without_tax);
                    const iVat = parseFloat(trx.invoice?.amount_without_vat);
                    const iAmt = parseFloat(trx.invoice?.amount);
                    const iTotal = parseFloat(trx.invoice?.total_with_vat) || parseFloat(trx.invoice?.total_amount);

                    const netAmt = aNet || iTax || iVat || iAmt || iTotal || 0;
                    const grossAmt = iTotal || iAmt || aNet || 0;

                    // Prioritize pending_balance if in pending mode
                    const displayAmt = isPendingRequest
                        ? (trx.pending_balance ?? grossAmt)
                        : netAmt;

                    grouped[invId].display_amount = displayAmt;
                    trx.expense_amount = grossAmt; // Add for label support
                    trx.total_amount = grossAmt;
                    trx.amount = netAmt;
                });

                setInvoices(Object.values(grouped).sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));
            }

        } catch (err) {
            console.error('Error fetching data:', err);
            setError('Error loading transaction history. ' + (err.response?.data?.message || err.message));
        } finally {
            setLoading(false);
        }
    };

    const fetchWebzashStatement = async (targetLedger, populateInvoices = false) => {
        // Use provided ledger or fallback to state
        const activeLedger = targetLedger || resolvedLedger || ledger;

        // If we don't have an ID, we can't fetch the statement
        if (!activeLedger?.id) return;

        setLoadingStatement(true);
        try {
            const params = new URLSearchParams();
            const lType = determineLedgerType(activeLedger);
            // For Balance Sheet items (Liabilities, Assets, Equity), we often want the Cumulative Closing Balance as of EndDate.
            // If we send startDate, we get the period movement, but Closing Balance *should* still be correct (Opening + Movement).
            // However, to align with the "Up To" transaction list view where we skipped startDate, let's also skip it here if it's that type.
            // Actually, if we skip startDate, Webzash might default to FY start or Beginning.
            // Let's match the fetchData logic: Skip startDate for Liability/Asset ledgers to ensure we get the full "Up To" picture.

            const isBSLedger = ['liability', 'vendor', 'sundry_creditors', 'employee', 'fixed_asset', 'payroll', 'vat_payable', 'capital', 'retained_earnings', 'bank', 'cash', 'advance'].includes(lType) || lType === 'expense' || activeLedger.name.includes('payable') || activeLedger.name.includes('receivable');

            // If it's a BS ledger (Liability/Asset), we want Cumulative Balance Up To EndDate.
            // Sending 'startDate' limits it to the period.
            // To get true cumulative, we should send a 'start of time' date.
            // If we omit it, some APIs default to FY Start. To be safe, let's explicitely ask for history.
            if (startDate) {
                params.append('startdate', startDate);
                params.append('start_date', startDate);
            }
            if (endDate) {
                params.append('enddate', endDate);
                params.append('end_date', endDate);
            }

            // Webzash report endpoint handles both ledgers and groups via ledger_id/group_id
            if (activeLedger.is_group) {
                params.append('group_id', activeLedger.id);
            } else {
                params.append('ledger_id', activeLedger.id);
            }

            console.log(`[Drilldown] Fetching Webzash statement for ${activeLedger.is_group ? 'group' : 'ledger'} ID: ${activeLedger.id}`);
            const response = await axios.get(`${API_BASE_URL}/api/reports/ledgerstatement`, {
                headers: {
                    'x-api-key': API_KEY,
                    'Accept': 'application/json'
                },
                params
            });

            if (response.data) {
                const data = response.data;
                const entries = data.entries || [];

                // Calculate totals if they are 0 or missing
                let totalDr = parseFloat(data.period_totals?.dr_total || data.total_debit || data.total_dr || 0);
                let totalCr = parseFloat(data.period_totals?.cr_total || data.total_credit || data.total_cr || 0);

                if (totalDr === 0 && totalCr === 0 && entries.length > 0) {
                    entries.forEach(e => {
                        const amt = parseFloat(e.Entryitem?.amount || 0);
                        if (e.Entryitem?.dc === 'D') totalDr += amt;
                        else totalCr += amt;
                    });
                }

                const enrichedData = {
                    ...data,
                    total_debit: totalDr,
                    total_credit: totalCr
                };

                setWebzashStatement(enrichedData);

                // Populate transaction summary table from Webzash entries
                if (populateInvoices === 'advance' || (populateInvoices === true && lType === 'advance')) {
                    processAdvanceStatement(enrichedData, activeLedger);
                } else if (populateInvoices === 'general' || (populateInvoices === true && lType !== 'advance')) {
                    processGeneralStatement(enrichedData, activeLedger);
                }
            }
        } catch (err) {
            console.error('Error fetching Webzash statement:', err);
        } finally {
            setLoadingStatement(false);
        }
    };

    const processGeneralStatement = (statementData, activeLedger) => {
        try {
            const entries = statementData.entries || [];

            // Filter only valid lines for this ledger
            let relevantLines = entries;
            if (!activeLedger.is_group) {
                relevantLines = entries.filter(e => String(e.Entryitem?.ledger_id) === String(activeLedger.id));
            }

            const mappedItems = relevantLines.map(item => {
                const amt = parseFloat(item.Entryitem?.amount || 0);
                return {
                    id: item.Entry.id,
                    ref_no: item.Entry.reference_no || item.Entry.number || `ENT-${item.Entry.id}`,
                    project_name: item.Entry.tag_name || (item.Entry.tag_id ? `Tag: ${item.Entry.tag_id}` : ''),
                    date: item.Entry.date,
                    transactions: [{
                        id: item.Entry.id,
                        date: item.Entry.date,
                        amount: amt,
                        display_amount: amt,
                        description: item.Entry.narration,
                        type: 'journal',
                        journal_id: item.Entry.id,
                        external_id: item.Entry.number,
                        dc: item.Entryitem?.dc,
                        status: 'journal'
                    }],
                    display_amount: amt,
                    status: 'journal'
                };
            });

            setInvoices(mappedItems.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));
        } catch (err) {
            console.error("Error processing general statement:", err);
        }
    };

    const processAdvanceStatement = (statementData, activeLedger) => {
        try {
            const entries = statementData.entries || [];
            const closingDC = statementData.closing_balance?.dc || 'D'; // Default to Debit (Asset)

            // 1. Identify Sources and Offsets
            // If Closing is D (Dr), then Debits are Sources (Advances Given), Credits are Offsets (Utilizations).
            // If Closing is C (Cr), then Credits are Sources (Advances Received), Debits are Offsets (Refunds/Utilizations).

            const sources = [];
            let offsetsTotal = 0;

            // Sort entries/lines by date ASC to apply FIFO correctly
            // The API usually returns them sorted, but good to ensure.
            // Note: statementData.entries is array of { Entry: {}, Entryitem: {} }

            // Filter only valid lines for this ledger
            // If it's a specific ledger, we filter strictly to avoid contra-entries if they exist.
            // If it's a group, we can't filter by ID easily (child IDs unknown), so we trust the API returned relevant lines.
            let relevantLines = entries;

            if (!activeLedger.is_group) {
                relevantLines = entries.filter(e => String(e.Entryitem.ledger_id) === String(activeLedger.id));
            }

            relevantLines = relevantLines.sort((a, b) => dayjs(a.Entry.date).unix() - dayjs(b.Entry.date).unix());

            console.log(`[Drilldown] Processing Advance Statement. Total Entries: ${entries.length}`, activeLedger);

            // Debug: Check IDs
            if (entries.length > 0) {
                console.log(`[Drilldown] First entry ID: ${entries[0].Entryitem?.ledger_id} vs Active Ledger ID: ${activeLedger.id}`);
            }

            relevantLines.forEach(line => {
                if (!line.Entryitem) return;

                // Strict Filter for Single Ledger
                // We MUST filter out contra lines (Bank/Expense) otherwise FIFO nets to 0.
                if (!activeLedger.is_group && String(line.Entryitem.ledger_id) !== String(activeLedger.id)) {
                    return;
                }

                // For Groups, we currently accept all lines (Risky - improvement needed if Group View is common)
                // But usually Drilldown is on Single Ledger.

                const amt = parseFloat(line.Entryitem.amount || 0);
                const dc = (line.Entryitem.dc || '').toUpperCase(); // Normalize DC

                // Determine if Source or Offset
                if (closingDC === 'D') {
                    if (dc === 'D') sources.push({ ...line, remaining: amt });
                    else offsetsTotal += amt;
                } else {
                    if (dc === 'C') sources.push({ ...line, remaining: amt });
                    else offsetsTotal += amt;
                }
            });

            console.log(`[Drilldown] Netting Info - ClosingDC: ${closingDC}, Sources: ${sources.length}, Offsets Total: ${offsetsTotal}`);

            // 2. Perform FIFO Netting
            const outstandingItems = [];
            for (const src of sources) {
                if (offsetsTotal >= src.remaining - 0.01) { // Tolerance for float precision
                    offsetsTotal -= src.remaining;
                    src.remaining = 0;
                    // Fully consumed
                } else {
                    src.remaining -= offsetsTotal;
                    offsetsTotal = 0;
                    if (src.remaining > 0.01) {
                        outstandingItems.push(src);
                    }
                }
            }

            console.log(`[Drilldown] Post-Netting Outstanding Items: ${outstandingItems.length}`);

            // 3. Map to Invoice/Transaction format for display
            const mappedItems = outstandingItems.map(item => ({
                id: item.Entry.id,
                ref_no: item.Entry.reference_no || item.Entry.number || `ENT-${item.Entry.id}`,
                project_name: item.Entry.tag_id ? `Tag: ${item.Entry.tag_id}` : '',
                date: item.Entry.date,
                transactions: [{
                    id: item.Entry.id,
                    date: item.Entry.date,
                    amount: parseFloat(item.Entryitem.amount),
                    pending_balance: item.remaining,
                    display_amount: item.remaining, // IMPORTANT: Use remaining for display
                    description: item.Entry.narration,
                    type: 'advance',
                    ref_no: item.Entry.reference_no
                }],
                display_amount: item.remaining,
                status: 'pending'
            }));

            console.log(`[Drilldown] Calculated ${mappedItems.length} outstanding advance items from statement.`);
            setInvoices(mappedItems.sort((a, b) => dayjs(b.date).unix() - dayjs(a.date).unix()));

        } catch (err) {
            console.error("Error processing advance statement:", err);
        }
    };

    // Helper function to determine ledger type from ledger data
    const determineLedgerType = (ledger) => {
        if (!ledger) return 'other';

        const ledgerName = ledger.name?.toLowerCase() || '';
        const groupName = ledger.group_name?.toLowerCase() || '';

        // Prioritize specific name-based routing for Sales Income/Returns
        // This must come before the explicit ledger.type check because sections like P&L pass generic types.
        if (ledgerName.includes('sales return')) return 'sales_return';
        if (ledgerName.includes('sales income')) return 'sales_income';

        // Check explicit type first if available and specific (Passed from P&L or other reports)
        if (ledger.type === 'vendor' || ledger.type === 'client' || ledger.type === 'expense' || ledger.type === 'income') {
            return ledger.type;
        }

        // Critical: Check for Bank Guarantees first
        if (ledgerName.includes('bank guarantee') || groupName.includes('bank guarantee') || ledgerName.includes('security deposit')) {
            return 'bank_guarantee';
        }

        // Restore Bank and Cash Checks
        if (groupName.includes('bank') || ledgerName.includes('bank account')) {
            return 'bank';
        }
        if (groupName.includes('cash') || ledgerName === 'cash' || ledgerName === 'petty cash') {
            return 'cash';
        }


        if (groupName.includes('sundry creditor') || groupName.includes('accounts payable')) {
            return 'vendor';
        }
        if (groupName.includes('sundry debtor') || groupName.includes('accounts receivable')) {
            return 'client';
        }

        // Strict Advance Check - Prioritize before generic Vendor/Client check if name clearly indicates advance
        if (ledgerName.includes('advance') || groupName.includes('advance') || ledgerName.includes('prepayment')) {
            return 'advance';
        }

        // Explicit Income/Revenue check
        if (groupName.includes('income') || groupName.includes('revenue') || ledgerName.includes('income') || ledgerName.includes('revenue') || ledgerName.includes('sales')) {
            return 'income';
        }

        // Explicit Expense check
        if (groupName.includes('expense') || ledgerName.includes('expense')) {
            return 'expense';
        }

        if (groupName.includes('vendor')) return 'vendor';
        if (groupName.includes('client') || groupName.includes('receivable')) return 'client';
        if (groupName.includes('loan') || groupName.includes('employee')) {
            return 'employee';
        }
        if (ledgerName.includes('drawing') || ledgerName.includes('withdrawal')) {
            return 'withdrawal';
        }
        if (ledgerName.includes('retained earnings') || ledgerName.includes('profit') || ledgerName.includes('loss')) {
            return 'retained_earnings';
        }
        if (groupName.includes('capital') || groupName.includes('owners') || groupName.includes('equity')) {
            return 'capital';
        }
        if (groupName.includes('fixed asset') ||
            ledgerName.includes('fixed asset') ||
            ledgerName.includes('furniture') ||
            ledgerName.includes('equipment') ||
            ledgerName.includes('electronics') ||
            ledgerName.includes('computer') ||
            ledgerName.includes('purchase')
        ) {
            return 'fixed_asset';
        }
        if (ledgerName.includes('vat') || groupName.includes('tax') || groupName.includes('vat')) {
            return 'vat_payable';
        }
        if (ledgerName.includes('salary') || ledgerName.includes('payroll') || ledgerName.includes('wages')) {
            return 'payroll';
        }
        if (groupName.includes('liabilit') || groupName.includes('payable')) {
            return 'liability';
        }
        if (groupName.includes('contra') || ledgerName.includes('transfer')) {
            return 'contra';
        }

        // Fallback: check ledger name patterns
        if (ledgerName.includes('vendor') || ledgerName.includes('supplier')) {
            return 'vendor';
        }
        if (ledgerName.includes('client') || ledgerName.includes('customer')) {
            return 'client';
        }
        if (ledgerName.includes('capital')) {
            return 'capital';
        }

        return 'other';
    };

    const fetchUsers = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/users`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            setUsers(response.data.users || []);
        } catch (e) { console.error('Error fetching users:', e); }
    };

    const fetchExpenseTypes = async () => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/expense-type`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            setExpenseTypes(response.data.expense || []);
        } catch (e) { console.error('Error fetching expense types:', e); }
    };

    const splitOrderByRevenueType = (order) => {
        const quotation = order.quotation || order;
        const subscriptionItems = [];
        const deploymentItems = [];
        quotation?.product_item_details?.forEach(item => {
            if (item.product?.license_type?.toLowerCase() === 'service') {
                deploymentItems.push(item);
            } else {
                subscriptionItems.push(item);
            }
        });
        return {
            subscription: { items: subscriptionItems, total: quotation?.total_amount_with_subscription || 0 },
            deployment: { items: deploymentItems, total: quotation?.deplyment_amount || 0 }
        };
    };

    const handleReferenceClick = async (inv, e) => {
        e.stopPropagation();
        const mainTrx = inv.transactions[0];
        const typeStr = String(mainTrx.type || '').toLowerCase();

        // 1. If it's an Invoice (Income)
        if (typeStr === 'income' || typeStr === 'invoice' || mainTrx.invoice_id) {
            handleInvoiceClick(mainTrx.invoice_id || mainTrx.id);
            return;
        }

        // 2. If it's an Expense
        if (typeStr === 'expense' || typeStr === 'bill' || mainTrx.expense_id) {
            const attachment = mainTrx.attachment || mainTrx.attatchment || mainTrx.invoice?.attachment || mainTrx.invoice?.attatchment;
            if (attachment) {
                handleDocPreview(mainTrx);
            } else {
                handleExpenseClick(mainTrx.expense_id || mainTrx.id, mainTrx);
            }
            return;
        }

        // 3. Fallback: If it has an attachment, show Doc Offcanvas
        if (mainTrx.attachment || mainTrx.attatchment || mainTrx.invoice?.attachment || mainTrx.invoice?.attatchment) {
            handleDocPreview(mainTrx);
        }
    };

    const handleDownloadTaxInvoicePDF = async (id, refNo) => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios({
                url: `${API_ORIGIN}/api/taxinvoice/${id}/download`,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });
            const blob = new Blob([response.data], { type: 'application/pdf' });
            saveAs(blob, `tax-invoice-${refNo || id}.pdf`);
        } catch (error) {
            console.error('Error downloading tax invoice:', error);
            Swal.fire('Error', 'Failed to download tax invoice PDF', 'error');
        }
    };

    const handleInvoiceClick = async (invoiceId) => {
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/taxinvoice/${invoiceId}`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });

            const invoiceData = response.data.data || response.data.taxinvoice;
            if (invoiceData) {
                setSelectedInvoice(invoiceData);
                const quotation = invoiceData.proforma_invoice?.quotation || invoiceData.proformaInvoice?.quotation;
                if (quotation) {
                    setSelectedQuotation({ ...quotation, revenueSplit: splitOrderByRevenueType(quotation) });
                    setCustomIntegration(quotation.custom_integration || false);
                    setCustomIntegrationDescription(quotation.custom_integration_description || '');
                    try {
                        const desc = quotation.editable_deployment_descriptions;
                        setEditableDeploymentIntegration(typeof desc === 'string' ? JSON.parse(desc) : (desc || {}));
                    } catch (err) { setEditableDeploymentIntegration({}); }
                }
                setShowPreviewModal(true);
            }
        } catch (error) {
            console.error("Error fetching invoice:", error);
            Swal.fire('Error', 'Failed to fetch invoice details', 'error');
        }
    };

    const handleExpenseClick = async (expenseId, trx) => {
        // If it was fetched as part of transaction list, it might have basic data.
        // But for modal we should fetch full data.
        try {
            const token = JSON.parse(localStorage.getItem('user-info'))?.token;
            const origin = localStorage.getItem('user-origin');
            const response = await axios.get(`${API_ORIGIN}/api/expense/${expenseId}`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin }
            });
            if (response.data?.data) {
                setSelectedExpenseData(response.data.data);
                setSelectedExpenseId(expenseId);
                setIsEditMode(false);
                setShowExpenseModal(true);
            }
        } catch (e) {
            console.error('Error fetching expense:', e);
            // Fallback: If full fetch fails, show doc if available
            if (trx.attachment || trx.attatchment) handleDocPreview(trx);
        }
    };

    const handleDocPreview = (entry) => {
        let path = entry.attachment || entry.attatchment || entry.invoice?.attachment || entry.invoice?.attatchment;
        if (!path) {
            console.warn('[Drilldown] handleDocPreview triggered but no attachment path found.', entry);
            return;
        }

        // Normalize path
        // 1. If it's a full URL, use it directly
        if (path.startsWith('http')) {
            setDocUrl(path);
            setDownloadUrl(path);
            setDocTitle(`Document: ${entry.ref_no || entry.invoice_no || entry.invoice?.ref_no || 'View'}`);
            setShowDocOffcanvas(true);
            return;
        }

        // 2. Handle relative paths
        // Remove leading slash to avoid double slashes with API_ORIGIN
        path = path.replace(/^\//, '');

        let displayPath = path;

        // Handle common Laravel storage path patterns
        if (path.startsWith('public/')) {
            displayPath = path.replace('public/', 'storage/');
        } else if (!path.startsWith('storage/')) {
            // Assume relative path needs storage prefix if not already present
            displayPath = `storage/${path}`;
        }

        const fileUrl = `${API_ORIGIN}/${displayPath}`;

        // For download, use API endpoint but ensure path matches backend expectation
        // If backend expects relative path (e.g. 'expenses/file.pdf'), pass 'path' as query param
        const downloadEndpoint = `${API_ORIGIN}/api/expenses/download/${path.split('/').pop()}?path=${encodeURIComponent(path)}`;

        setDocUrl(fileUrl);
        setDownloadUrl(downloadEndpoint);
        setDocTitle(`Document: ${entry.ref_no || entry.invoice_no || entry.invoice?.ref_no || 'View'}`);
        setShowDocOffcanvas(true);
    };

    const DocPreviewOffcanvas = ({ show, onHide, url, downloadUrl, title }) => (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '1050px' }}>
            <Offcanvas.Header closeButton className="border-bottom bg-gradient py-2" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                <div className="d-flex align-items-center justify-content-between w-100 pe-4">
                    <Offcanvas.Title className="fw-bold fs-6 mb-0 text-white d-flex align-items-center gap-2">
                        <FiFileText size={18} />
                        <span className="text-truncate" style={{ maxWidth: '400px' }}>{title}</span>
                    </Offcanvas.Title>
                    <Button
                        variant="light"
                        size="sm"
                        className="d-flex align-items-center gap-2 shadow-sm rounded-pill px-3 fw-medium"
                        style={{ fontSize: '0.8rem' }}
                        onClick={async () => {
                            try {
                                const response = await axios.get(downloadUrl, {
                                    responseType: 'blob',
                                    headers: {
                                        Authorization: `Bearer ${JSON.parse(localStorage.getItem('user-info'))?.token}`,
                                        'Tenant-Origin': localStorage.getItem('user-origin')
                                    }
                                });
                                const fileName = title.replace(/[:\\/*?"<>|]/g, '_') + (url.endsWith('.pdf') ? '.pdf' : '');
                                saveAs(response.data, fileName);
                            } catch (error) {
                                console.error("Download error:", error);
                                window.open(url, '_blank');
                            }
                        }}
                    >
                        <FiDownload size={14} className="text-primary" />
                        <span className="text-primary">Download</span>
                    </Button>
                </div>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0">
                {url ? (
                    <iframe
                        src={`${url}#toolbar=0&navpanes=0&scrollbar=1`}
                        title={title}
                        width="100%"
                        height="100%"
                        style={{ border: 'none' }}
                        frameBorder="0"
                    />
                ) : (
                    <div className="d-flex align-items-center justify-content-center h-100">
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" role="status"></div>
                            <p className="text-muted">Loading document...</p>
                        </div>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );

    const toggleExpand = async (invId, trxList) => {
        const newExpanded = new Set(expandedInvoices);
        if (newExpanded.has(invId)) {
            newExpanded.delete(invId);
        } else {
            newExpanded.add(invId);
            // Fetch entry details for all transactions and their related journal entries
            for (const trx of trxList) {
                console.log('[Drilldown] Transaction Object:', trx);
                if (trx.entry_id && !entryDetails[trx.entry_id]) {
                    fetchWebzashEntry(trx.entry_id, { skipLoader: true });
                }

                // Fetch related Journal Entry (Accrual)
                const journalId = trx.invoice?.tax_invoices?.[0]?.journal_id || trx.invoice?.journal_id || trx.expense?.journal_id;
                if (journalId && !entryDetails[journalId]) {
                    fetchWebzashEntry(journalId, { skipLoader: true });
                }

                // Check transaction_expense_items for multiple expenses
                if (trx.transaction_expense_items?.length > 0) {
                    trx.transaction_expense_items.forEach(expItem => {
                        const expJournalId = expItem.expense?.journal_id;
                        if (expJournalId && !entryDetails[expJournalId]) {
                            fetchWebzashEntry(expJournalId, { skipLoader: true });
                        }
                    });
                }

                // NEW: Proactively fetch direct journal/entry links for total transparency
                if (trx.journal_id && !entryDetails[trx.journal_id]) {
                    fetchWebzashEntry(trx.journal_id, { skipLoader: true });
                }
                if (trx.reverse_entry_id && !entryDetails[trx.reverse_entry_id]) {
                    fetchWebzashEntry(trx.reverse_entry_id, { skipLoader: true });
                }
            }
        }
        setExpandedInvoices(newExpanded);
    };

    const fetchWebzashEntry = async (entryId, { skipLoader = false } = {}) => {
        if (!skipLoader && fetchingEntry === entryId) return;
        if (!skipLoader) setFetchingEntry(entryId);

        try {
            const webzashHeaders = { 'x-api-key': API_KEY, 'Accept': 'application/json' };
            console.log(`[Drilldown] Lazy-loading Webzash entry: ${entryId}${skipLoader ? ' (Background)' : ''}`);

            const types = ['journal', 'receipt', 'payment', 'contra'];
            let fetchedData = null;

            for (const type of types) {
                try {
                    const response = await axios.get(`${API_BASE_URL}/api/entries/view/${type}/${entryId}`, {
                        headers: webzashHeaders,
                        skipLoader: true,
                    });
                    if (response.data && (response.data.entry || response.data.Entry)) {
                        fetchedData = response.data;
                        break;
                    }
                } catch (e) {
                    continue;
                }
            }

            if (fetchedData) {
                setEntryDetails(prev => ({ ...prev, [entryId]: fetchedData }));
            }
        } catch (err) {
            console.error(`Failed to fetch entry ${entryId}:`, err);
        } finally {
            if (!skipLoader) setFetchingEntry(null);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        }).format(amount);
    };


    const getStatusBadge = (status) => {
        const s = status?.toLowerCase() || '';
        if (s.includes('paid')) return <Badge bg="success" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Paid</Badge>;
        if (s.includes('partial')) return <Badge bg="warning" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Partial</Badge>;
        if (s === 'journal') return <Badge bg="light" text="dark" className="border rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>Journal</Badge>;
        return <Badge bg="danger" className="rounded-1 text-uppercase" style={{ fontSize: '0.6rem' }}>{status || 'Pending'}</Badge>;
    };

    const renderNestedDetails = (inv) => {
        const journals = [];
        const payments = [];
        const seenEntryIds = new Set();

        inv.transactions.forEach(trx => {
            // For EXPENSE records from project_expenses, show ONLY the expense journal
            if (trx.expense_id && trx.journal_id) {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Expense Journal',
                        logId: trx.external_id || trx.invoice_no || `E-${trx.expense_id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                // Skip all other entries for expense records
                return;
            }

            // For INVOICE records from proforma_invoices, show ONLY the invoice journal
            if (trx.invoice_id && trx.journal_id && trx.type === 'income') {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Invoice Journal',
                        logId: trx.ref_no || `INV-${trx.invoice_id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                // Skip all other entries for invoice records
                return;
            }

            // For CREDIT NOTE records (income_reversal)
            if (trx.journal_id && trx.type === 'income_reversal') {
                if (!seenEntryIds.has(trx.journal_id)) {
                    journals.push({
                        id: trx.journal_id,
                        type: 'Credit Note Journal',
                        logId: trx.ref_no || `CN-${trx.id}`
                    });
                    seenEntryIds.add(trx.journal_id);
                }
                return;
            }

            // For TRANSACTION records, use the original logic
            // 1. Core accounting links from transaction itself
            if (trx.journal_id && !seenEntryIds.has(trx.journal_id)) {
                journals.push({
                    id: trx.journal_id,
                    type: 'Journal',
                    logId: trx.external_id || 'JN'
                });
                seenEntryIds.add(trx.journal_id);
            }

            // 2. Accrual/Journal Entry (Standard Relation paths)
            const mainJournalId = trx.invoice?.tax_invoices?.[0]?.journal_id || trx.invoice?.journal_id || trx.expense?.journal_id;
            if (mainJournalId && !seenEntryIds.has(mainJournalId)) {
                journals.push({
                    id: mainJournalId,
                    type: 'Journal',
                    logId: trx.invoice?.ref_no || trx.ref_no || 'JN'
                });
                seenEntryIds.add(mainJournalId);
            }

            // 3. Check transaction_expense_items for expenses
            if (trx.transaction_expense_items?.length > 0) {
                trx.transaction_expense_items.forEach(expItem => {
                    const expJournalId = expItem.expense?.journal_id;
                    if (expJournalId && !seenEntryIds.has(expJournalId)) {
                        journals.push({
                            id: expJournalId,
                            type: 'Journal',
                            logId: expItem.expense?.external_id || 'JN'
                        });
                        seenEntryIds.add(expJournalId);
                    }
                });
            }

            // 4. Financial Transaction Entry
            if (trx.entry_id && !seenEntryIds.has(trx.entry_id)) {
                payments.push({
                    id: trx.entry_id,
                    type: trx.type || 'Payment',
                    logId: trx.external_id || '0'
                });
                seenEntryIds.add(trx.entry_id);
            }

            // 5. Reversal entries
            if (trx.reverse_entry_id && !seenEntryIds.has(trx.reverse_entry_id)) {
                payments.push({
                    id: trx.reverse_entry_id,
                    type: 'Reversal',
                    logId: trx.external_id || 'REV'
                });
                seenEntryIds.add(trx.reverse_entry_id);
            }
        });

        const renderEntry = (entry, eIdx) => {
            const webzashData = entryDetails[entry.id];
            return (
                <div key={eIdx} className="mb-3 bg-white rounded-3 shadow-sm border-0 overflow-hidden compact-journal-card animate-slide-up" style={{ animationDelay: `${eIdx * 0.05}s` }}>
                    <div className="px-3 py-2 bg-light bg-opacity-40 border-bottom d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center gap-3">
                            <Badge bg={entry.type ? (entry.type.toLowerCase().includes('journal') ? "info" : "secondary") : "secondary"} className="text-uppercase ls-1 px-2 py-1" style={{ fontSize: '0.6rem', fontWeight: '800' }}>
                                {entry.type}
                            </Badge>
                            <span className="text-muted fw-bold" style={{ fontSize: '0.6rem' }}>REF: <span className="text-dark">#{entry.logId}</span></span>
                        </div>
                    </div>

                    {fetchingEntry === entry.id ? (
                        <div className="text-center py-4 bg-white"><Spinner animation="border" size="sm" variant="primary" /></div>
                    ) : webzashData ? (
                        <div className="bg-white">
                            <div className="d-flex flex-column py-2">
                                {(webzashData.entryitems || webzashData.Entryitem || []).map((item, i) => (
                                    <div key={i} className={`d-flex align-items-center px-4 py-2 my-1 mx-2 rounded-3 ${item.ledger_name === ledger?.name ? 'target-ledger-row' : 'ledger-row-clean'}`}>
                                        <div className="flex-grow-1">
                                            <div className={`fw-bold ${item.ledger_name === ledger?.name ? 'text-primary' : 'text-dark'}`} style={{ fontSize: '0.85rem' }}>
                                                {item.ledger_name || item.Ledger?.name}
                                                {item.ledger_name === ledger?.name && <Badge bg="primary" className="ms-2 px-1 py-0 rounded-pill" style={{ fontSize: '10px' }}>Target</Badge>}
                                            </div>
                                        </div>
                                        <div className="text-end d-flex gap-4" style={{ width: '280px' }}>
                                            <div className={`amount-badge text-end flex-grow-1 ${item.dc === 'D' ? 'text-danger' : 'text-lighter'}`} style={{ width: '130px', fontSize: '0.85rem' }}>
                                                {item.dc === 'D' ? formatCurrency(item.amount || item.dr_amount) : '—'}
                                            </div>
                                            <div className={`amount-badge text-end flex-grow-1 ${item.dc === 'C' ? 'text-success' : 'text-lighter'}`} style={{ width: '130px', fontSize: '0.85rem' }}>
                                                {item.dc === 'C' ? formatCurrency(item.amount || item.cr_amount) : '—'}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                            <div className="px-4 py-2 mt-1 bg-light bg-opacity-10 border-top d-flex align-items-center">
                                <small className="text-muted text-uppercase fw-bold ls-1 me-3" style={{ fontSize: '0.6rem' }}>Narrative</small>
                                <p className="mb-0 text-dark fw-medium" style={{ fontSize: '0.75rem' }}>{(webzashData.entry || webzashData.Entry)?.narration || 'No narrative provided.'}</p>
                            </div>
                        </div>
                    ) : (
                        <div className="p-4 text-center text-muted x-small fst-italic">Waiting for journal synchronization...</div>
                    )}
                </div>
            );
        };

        return (
            <Tabs defaultActiveKey={journals.length > 0 ? "journal" : "payments"} className="custom-ledger-tabs mb-4 px-2 border-0">
                <Tab eventKey="journal" title={<span className="d-flex align-items-center"><FiFileText className="me-2" /> Journal</span>} disabled={journals.length === 0}>
                    <div className="pt-3">
                        {journals.length > 0 ? journals.map((entry, idx) => renderEntry(entry, idx)) : (
                            <div className="text-center py-4 bg-white rounded-3 border dashed muted small">No accrual journal linked to this transaction Group.</div>
                        )}
                    </div>
                </Tab>
                <Tab eventKey="payments" title={<span className="d-flex align-items-center"><FiCheckCircle className="me-2" /> Financial Transactions</span>} disabled={payments.length === 0}>
                    <div className="pt-3">
                        {payments.length > 0 ? payments.map((entry, idx) => renderEntry(entry, idx)) : (
                            <div className="text-center py-4 bg-white rounded-3 border dashed muted small">No direct payment/receipt entries found.</div>
                        )}
                    </div>
                </Tab>
            </Tabs>
        );
    };

    const renderWebzashStatement = () => {
        if (loadingStatement) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
        if (!webzashStatement || !webzashStatement.entries || webzashStatement.entries.length === 0) {
            return (
                <div className="text-center py-5 opacity-50">
                    <FiFileText className="fs-1 mb-3" />
                    <p>No bookkeeping entries found in Webzash for this period.</p>
                </div>
            );
        }

        return (
            <Card className="border-0 shadow-sm overflow-hidden rounded-4">
                <div className="table-responsive">
                    <Table hover className="mb-0 align-middle ledger-table">
                        <thead className="bg-light border-bottom">
                            <tr>
                                <th className="ps-4 py-3 border-0 text-muted small text-uppercase ls-1">Date</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1">Narration</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1 text-end">Debit</th>
                                <th className="py-3 border-0 text-muted small text-uppercase ls-1 text-end">Credit</th>
                                <th className="pe-4 py-3 border-0 text-end text-muted small text-uppercase ls-1">Ref</th>
                            </tr>
                        </thead>
                        <tbody>
                            {webzashStatement.entries.map((entry, idx) => {
                                // Extract the core entry data
                                const ent = entry.Entry || {};
                                const item = entry.Entryitem || {};

                                return (
                                    <tr key={idx} className="ledger-row">
                                        <td className="ps-4">
                                            <div className="fw-medium" style={{ fontSize: '0.85rem' }}>{dayjs(ent.date).format('DD MMM YYYY')}</div>
                                        </td>
                                        <td className="py-3">
                                            <div className="text-dark fw-medium mb-1" style={{ fontSize: '0.85rem', maxWidth: '350px' }}>{ent.narration}</div>
                                            <Badge bg="light" text="dark" className="border rounded-pill x-small text-uppercase ls-1" style={{ fontWeight: 800 }}>Ref: #{ent.number}</Badge>
                                        </td>
                                        <td className="text-end">
                                            {item.dc === 'D' ? <span className="text-danger fw-bold">{formatCurrency(item.amount)}</span> : '—'}
                                        </td>
                                        <td className="text-end">
                                            {item.dc === 'C' ? <span className="text-success fw-bold">{formatCurrency(item.amount)}</span> : '—'}
                                        </td>
                                        <td className="pe-4 text-end">
                                            <span className="text-muted x-small">#{ent.id}</span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>
            </Card>
        );
    };

    const determineDC = (trx) => {
        if (trx.dc) return trx.dc === 'D' ? 'DR' : 'CR';
        const lType = determineLedgerType(resolvedLedger || ledger);

        // Liability/Equity/Income types: Expenses increase (CR), Payments decrease (DR)
        if (['liability', 'vendor', 'payroll', 'vat_payable', 'capital', 'retained_earnings'].includes(lType)) {
            if (trx.type === 'advance') {
                return (trx.advance_type === 'vendor') ? 'DR' : 'CR'; // Advance to vendor is DR (reduces liability)
            }
            if (['expense', 'payroll', 'salary', 'vat_payable'].includes(trx.type)) return 'CR';
            if (['payment', 'receipt'].includes(trx.type)) return 'DR';
        }

        // Asset/Expense types: Invoices increase (DR), Receipts decrease (CR)
        if (['client', 'bank', 'cash', 'fixed_asset', 'employee'].includes(lType)) {
            if (trx.type === 'advance') {
                return (trx.advance_type === 'client' || !trx.advance_type) ? 'CR' : 'DR'; // Advance from client is CR (reduces receivable)
            }
            if (['income', 'invoice'].includes(trx.type)) return 'DR';
            if (['payment', 'receipt', 'expense'].includes(trx.type)) return 'CR';
        }
        return '';
    };

    // Calculate balance from the list whenever invoices change
    useEffect(() => {
        if (!invoices || invoices.length === 0) {
            setCalculatedBalance(0);
            return;
        }

        const lType = determineLedgerType(resolvedLedger || ledger);
        // Liability/Receivable: Balance = Amount - Paid (Outstanding)
        const isOutstandingType = ['liability', 'vendor', 'sundry_creditors', 'employee', 'sundry_debtors', 'client', 'payroll', 'vat_payable'].includes(lType) || (resolvedLedger || ledger).name.toLowerCase().includes('payable') || (resolvedLedger || ledger).name.toLowerCase().includes('receivable');

        // Asset/Expense: Balance = Sum of Amounts (Accumulation)
        // Fixed Asset, Expense, Bank, Cash, Capital (usually just sum of txns)
        // For simple drilldown list, just sum the display amounts or base amounts.

        let total = 0;

        if (isOutstandingType) {
            total = invoices.reduce((sum, item) => {
                const firstTrx = item.transactions[0];
                // Use pending_balance or calculate it.
                // Note: paid_amount might be global total paid. 
                // If we want "Balance existing AT THAT DATE", we strictly need date-based payments.
                // But list usually implies current state of those specific bills.
                // Ideally this sums the "Remaining Balance" of the bills in the list.
                const amount = parseFloat(firstTrx.pending_balance ?? (firstTrx.amount - (firstTrx.paid_amount || 0)));
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);
        } else if (!['bank', 'cash', 'fixed_asset', 'capital', 'other'].includes(lType)) {
            // For Fixed Assets / Expenses: Simply sum the Amounts.
            total = invoices.reduce((sum, item) => {
                const firstTrx = item.transactions[0];
                const amount = parseFloat(firstTrx.amount || 0); // Gross Amount
                return sum + (isNaN(amount) ? 0 : amount);
            }, 0);
        } else {
            // Force fallback to Webzash statement closing balance
            total = null;
        }

        setCalculatedBalance(total);
    }, [invoices, resolvedLedger, ledger]);

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '950px' }} className="ledger-details-offcanvas shadow-lg">
            <Offcanvas.Header closeButton className="border-bottom sticky-top py-4 px-4 header-gradient">
                {/* ... Header Content ... */}
                <Offcanvas.Title className="w-100">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="bg-white bg-opacity-20 p-2 rounded-4 me-3 d-flex align-items-center justify-content-center shadow-sm backdrop-blur" style={{ width: '50px', height: '50px' }}>
                                <FiInfo className="text-black fs-3" />
                            </div>
                            <div>
                                <div className="fs-4 fw-bold text-black mb-0 tracking-tight">{ledger?.name}</div>
                                <div className="d-flex align-items-center text-black text-opacity-75" style={{ fontSize: '0.8rem', marginTop: '2px' }}>
                                    <FiExternalLink className="me-1" size={12} />
                                    <span>Detailed Ledger Analysis & Reconciliation</span>
                                </div>
                            </div>
                        </div>
                        <div className="d-none d-md-block pe-5">
                            <Badge bg="white" text="primary" className="rounded-pill px-3 py-2 shadow-sm border-0">
                                <FiHash className="me-1" /> Ledger View
                            </Badge>
                        </div>
                    </div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="p-0 bg-soft-blue">
                <div className="p-4 bg-white border-bottom shadow-sm mb-4">
                    <Row className="g-3">
                        <Col md={12}>
                            <div className="d-flex gap-3 mb-3">
                                <div className="p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden">
                                    <div className={`position-absolute top-0 start-0 h-100 ${webzashStatement?.opening_balance?.dc === 'D' ? 'bg-danger' : 'bg-success'}`} style={{ width: '4px', opacity: 0.5 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Opening Balance</small>
                                    <div className="fw-bold h5 mb-0">
                                        {formatCurrency(webzashStatement?.opening_balance?.amount || 0)} <span className="small opacity-50">{webzashStatement?.opening_balance?.dc}r</span>
                                    </div>
                                </div>
                                <div className="p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden">
                                    <div className="position-absolute top-0 start-0 h-100 bg-danger" style={{ width: '4px', opacity: 0.2 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Total Debit</small>
                                    <div className="fw-bold h5 mb-0 text-danger">
                                        {formatCurrency(webzashStatement?.total_debit || 0)}
                                    </div>
                                </div>
                                <div className="p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden">
                                    <div className="position-absolute top-0 start-0 h-100 bg-success" style={{ width: '4px', opacity: 0.2 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Total Credit</small>
                                    <div className="fw-bold h5 mb-0 text-success">
                                        {formatCurrency(webzashStatement?.total_credit || 0)}
                                    </div>
                                </div>
                                <div className="p-3 border rounded-3 bg-white bg-opacity-10 flex-grow-1 shadow-sm position-relative overflow-hidden" style={{ borderLeft: '4px solid #0d6efd !important' }}>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Closing Balance</small>
                                    <div className="fw-bold h4 mb-0 text-primary">
                                        {/* Prioritize Statement Balance (Truth), then Calculated (Specific/Filtered), then Ledger Current (Fallback) */}
                                        {formatCurrency(
                                            (!projectId && webzashStatement?.closing_balance)
                                                ? webzashStatement.closing_balance.amount
                                                : (calculatedBalance !== null
                                                    ? calculatedBalance
                                                    : (webzashStatement?.closing_balance ? webzashStatement.closing_balance.amount : ((resolvedLedger || ledger)?.current_balance || 0)))
                                        )}
                                        {/* Use Statement DC or default to DR for calculated (Asset/Exp) or Match Ledger */}
                                        <Badge bg={(webzashStatement?.closing_balance?.dc || (resolvedLedger || ledger)?.dc || 'D') === 'D' ? 'danger' : 'success'} className="ms-2 px-2 py-1" style={{ fontSize: '0.6rem' }}>
                                            {(webzashStatement?.closing_balance?.dc || (resolvedLedger || ledger)?.dc || 'D') === 'D' ? 'DEBIT' : 'CREDIT'}
                                        </Badge>
                                    </div>
                                </div>
                                {/* Spacer to keep layout intact if needed, or remove */}
                                {/* Third card removed as it is now redundant with Closing Balance */}
                                {/* <div className="p-3 border rounded-3 bg-white flex-grow-1 shadow-sm position-relative overflow-hidden text-end">
                                    <div className={`position-absolute top-0 end-0 h-100 ${webzashStatement?.closing_balance?.dc === 'D' ? 'bg-danger' : 'bg-success'}`} style={{ width: '4px', opacity: 0.5 }}></div>
                                    <small className="text-muted d-block mb-1 text-uppercase fw-bold ls-1" style={{ fontSize: '0.6rem' }}>Statement Closing</small>
                                    <div className="fw-bold h5 mb-0">
                                        {formatCurrency(webzashStatement?.closing_balance?.amount || 0)} <span className="small opacity-50">{webzashStatement?.closing_balance?.dc}r</span>
                                    </div>
                                </div> */}
                            </div>
                        </Col>
                        <Col md={12}>
                            <div className="p-2 border rounded-3 bg-light d-flex align-items-center justify-content-center gap-4">
                                <div className="d-flex align-items-center gap-2">
                                    <FiCalendar className="text-muted" />
                                    <span className="small fw-bold text-dark">
                                        {startDate ? dayjs(startDate).format('MMM D, YYYY') : 'Earliest'}
                                        <FiChevronRight size={10} className="mx-1" />
                                        {endDate ? dayjs(endDate).format('MMM D, YYYY') : 'Latest'}
                                    </span>
                                </div>
                                <div className="border-start h-100 mx-2" style={{ width: '1px', height: '20px', backgroundColor: '#e2e8f0' }}></div>
                            </div>
                        </Col>
                        {/* No toggle anymore */}
                    </Row>
                </div>

                <div className="px-4 pb-5">
                    <Tabs
                        activeKey={activeTab}
                        onSelect={(k) => setActiveTab(k)}
                        className="custom-main-tabs mb-4 px-2"
                    >
                        <Tab
                            eventKey="summary"
                            title={
                                <span className="d-flex align-items-center">
                                    <FiBriefcase className="me-2" />
                                    Transaction Summary
                                    {invoices.length > 0 && <Badge bg="primary" pill className="ms-2 small">{invoices.length}</Badge>}
                                </span>
                            }
                        >
                            <div className="pt-3">
                                {loading ? (
                                    <div className="text-center py-5">
                                        <Spinner animation="border" variant="primary" size="lg" className="mb-3" />
                                        <h6 className="text-muted fw-normal">Analyzing transaction patterns...</h6>
                                    </div>
                                ) : error ? (
                                    <Alert variant="danger" className="border-0 shadow-sm rounded-3 p-4">
                                        <div className="d-flex align-items-center">
                                            <FiInfo className="me-3 fs-3" />
                                            <div><strong>Error:</strong> {error}</div>
                                        </div>
                                    </Alert>
                                ) : invoices.length === 0 ? (
                                    <div className="text-center py-5 px-3 bg-white border dashed rounded-4 shadow-sm opacity-75">
                                        <FiBriefcase className="fs-1 mb-3 text-muted opacity-25" />
                                        <h5 className="text-dark fw-bold">No Transactions</h5>
                                    </div>
                                ) : (
                                    <div className="ledger-card-container">
                                        {/* Original Table View */}
                                        <Card className="border-0 shadow-sm overflow-hidden rounded-4">
                                            <div className="table-responsive">
                                                <Table hover className="mb-0 align-middle ledger-table">
                                                    <thead className="bg-light border-bottom">
                                                        <tr>
                                                            <th className="ps-4 py-3 border-0 text-muted small text-uppercase ls-1">Date</th>
                                                            <th className="py-3 border-0 text-muted small text-uppercase ls-1" style={{ width: '180px' }}>Reference No</th>
                                                            <th className="py-3 border-0 text-muted small text-uppercase ls-1">Project</th>
                                                            <th className="py-3 border-0 text-end text-muted small text-uppercase ls-1">Amount (AED)</th>
                                                            <th className="pe-4 py-3 border-0" style={{ width: '50px' }}></th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {invoices.map((inv) => {
                                                            const mainTrx = inv.transactions[0];
                                                            const dc = determineDC(mainTrx);
                                                            const isPending = pendingOnly;

                                                            // For expense transactions, prioritize Net Amount (amount) for the main column
                                                            const isExpenseType = ['expense', 'payroll', 'salary', 'vat_payable', 'income_reversal'].includes(mainTrx.type);
                                                            const baseAmount = isExpenseType && mainTrx.amount !== undefined
                                                                ? mainTrx.amount
                                                                : inv.display_amount;

                                                            // Logic Update:
                                                            // If we have a startDate (meaning it's a historical/period view), we want to see the Original Amount (baseAmount).
                                                            // We only show pending_balance if we are practically in "Pending Only" mode without date overrides.
                                                            // Also, if the item is fully paid, pending_balance is 0, which looks wrong in a history list.
                                                            const showPendingBalance = isPending && !startDate && mainTrx.pending_balance !== undefined;

                                                            const displayAmt = showPendingBalance
                                                                ? mainTrx.pending_balance
                                                                : baseAmount;

                                                            const paidAmount = mainTrx.paid_amount || 0;
                                                            const isPartiallyPaid = paidAmount > 0 && paidAmount < baseAmount;

                                                            return (
                                                                <React.Fragment key={inv.id}>
                                                                    <tr
                                                                        className={`ledger-row cursor-pointer transition-all ${expandedInvoices.has(inv.id) ? 'expanded-row' : ''}`}
                                                                        onClick={() => toggleExpand(inv.id, inv.transactions)}
                                                                    >
                                                                        <td className="ps-4 timeline-col">
                                                                            <div className="timeline-point"></div>
                                                                            <div className="fw-mute date-primary" style={{ fontSize: '0.9rem' }}>{dayjs(inv.date).format('DD MMM YYYY')}</div>
                                                                        </td>
                                                                        <td className="py-4">
                                                                            <div
                                                                                className="text-primary-hover mb-0 record-ref fw-bold"
                                                                                style={{ fontSize: '0.85rem', cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: '3px', textDecorationColor: 'rgba(59, 130, 246, 0.3)' }}
                                                                                onClick={(e) => handleReferenceClick(inv, e)}
                                                                            >
                                                                                <FiExternalLink className="me-1 small opacity-50" />
                                                                                {inv.ref_no}
                                                                            </div>
                                                                        </td>
                                                                        <td className="py-4">
                                                                            <div className="text-secondary fw-medium" style={{ fontSize: '0.85rem' }}>{inv.project_name}</div>
                                                                        </td>
                                                                        <td className="text-end py-4">
                                                                            <div className="d-flex align-items-center justify-content-end gap-2">
                                                                                <div className="fw-mute amount-text" style={{ fontSize: '0.95rem' }}>{formatCurrency(displayAmt)}</div>
                                                                                {dc && <span className={`badge x-small border rounded-1 p-1 ${dc === 'CR' ? 'bg-success bg-opacity-10 text-success' : 'bg-danger bg-opacity-10 text-danger'}`} style={{ fontSize: '0.65rem', minWidth: '25px' }}>{dc}</span>}
                                                                            </div>
                                                                            {isPending && isPartiallyPaid && (
                                                                                <div className="text-muted x-small mt-1">
                                                                                    Paid: {formatCurrency(paidAmount)} | Pending: {formatCurrency(displayAmt)}
                                                                                </div>
                                                                            )}
                                                                            {!isPending && isExpenseType && mainTrx.expense_amount && (
                                                                                <div className="text-muted x-small mt-1">
                                                                                    Total: {formatCurrency(mainTrx.expense_amount)}
                                                                                </div>
                                                                            )}
                                                                        </td>
                                                                        <td className="pe-4 text-end py-4">
                                                                            <div className={`chevron-btn ${expandedInvoices.has(inv.id) ? 'is-active' : ''}`}>
                                                                                <FiChevronRight />
                                                                            </div>
                                                                        </td>
                                                                    </tr>
                                                                    <tr>
                                                                        <td colSpan="5" className="p-0 border-0">
                                                                            <Collapse in={expandedInvoices.has(inv.id)}>
                                                                                <div className="bg-light p-4 overflow-hidden" style={{ boxShadow: 'inset 0 2px 15px rgba(0,0,0,0.06)' }}>
                                                                                    <div className="d-flex align-items-center mb-4">
                                                                                        <h6 className="mb-0 fw-bold text-dark me-3">Details</h6>
                                                                                        <div className="flex-grow-1 border-bottom border-secondary border-opacity-25" style={{ height: '0px' }}></div>
                                                                                    </div>
                                                                                    {/* Inner Entry Details Logic Here */}
                                                                                    {renderNestedDetails(inv)}
                                                                                </div>
                                                                            </Collapse>
                                                                        </td>
                                                                    </tr>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </tbody>
                                                </Table>
                                            </div>
                                        </Card>
                                    </div>
                                )}
                            </div>
                        </Tab>
                        {/* Ledger Statement Tab Removed as requested */}
                        {/* {!isLiabilityLedger && (
                            <Tab
                                eventKey="statement"
                                title={
                                    <span className="d-flex align-items-center">
                                        <FiFileText className="me-2" />
                                        Ledger Statement (Webzash)
                                    </span>
                                }
                            >
                                <div className="pt-3">
                                    {renderWebzashStatement()}
                                </div>
                            </Tab>
                        )} */}
                    </Tabs>
                </div>

                <style>{`
                    .backdrop-blur { backdrop-filter: blur(8px); border: 1px solid rgba(255,255,255,0.1); }
                    .bg-soft-blue { background-color: #f8fafc; }
                    .fw-black { font-weight: 800; }
                    .tracking-tighter { letter-spacing: -0.04em; }
                    .tracking-tight { letter-spacing: -0.02em; }
                    .ls-1 { letter-spacing: 0.05em; }
                    .x-small { font-size: 0.7rem; }
                    .text-lighter { color: #e2e8f0; }

                    /* Main Tabs */
                    .custom-main-tabs .nav-link { 
                        color: #64748b; font-weight: 700; font-size: 0.9rem; padding: 1rem 1.5rem; border: none !important; transition: all 0.3s ease; opacity: 0.6; position: relative;
                    }
                    .custom-main-tabs .nav-link:hover { opacity: 1; color: #3b82f6; }
                    .custom-main-tabs .nav-link.active { color: #3b82f6; opacity: 1; background: #fff !important; border-radius: 12px 12px 0 0; box-shadow: 0 -4px 15px rgba(0,0,0,0.03); }
                    .custom-main-tabs .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 15%; width: 70%; height: 3px; background: #3b82f6; border-radius: 3px; }

                    .border-bottom-soft { border-bottom: 1px solid #f1f5f9; }
                    .compact-ledger-table th { background: #1e293b !important; color: #f8fafc !important; }
                    .dashed { border-style: dashed !important; }

                    .stat-card { transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1); border: 1px solid #f1f5f9 !important; }
                    .stat-card:hover { transform: translateY(-4px); border-color: #e2e8f0 !important; }
                    .card-accent { position: absolute; top: 0; left: 0; width: 6px; height: 100%; border-radius: 4px 0 0 4px; }
                    .shadow-inner { box-shadow: inset 0 2px 4px 0 rgba(0, 0, 0, 0.05); }
                    
                    .ledger-table thead th { font-weight: 700; color: #64748b; background-color: #f8fafc; padding: 1.25rem 0.75rem; }
                    .ledger-row { border-bottom: 1px solid #f1f5f9; position: relative; transition: all 0.2s ease !important; }
                    .ledger-row:hover { background-color: #fff !important; transform: scale(1.005); box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.05), 0 4px 6px -2px rgba(0, 0, 0, 0.02); z-index: 10; }
                    .expanded-row { background-color: #f1f5f9 !important; }
                    
                    .timeline-col { position: relative; }
                    .timeline-point { position: absolute; left: 1.25rem; top: 50%; transform: translateY(-50%); width: 10px; height: 10px; background: #3b82f6; border: 2px solid #fff; border-radius: 50%; box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); z-index: 2; }
                    .date-primary { padding-left: 1.25rem; }
                    
                    .chevron-btn { width: 32px; height: 32px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: #f1f5f9; color: #64748b; transition: all 0.3s ease; }
                    .is-active { background: #3b82f6; color: #fff; transform: rotate(90deg); }
                    .text-primary-hover { color: #64748b; transition: all 0.2s; }
                    .text-primary-hover:hover { color: #3b82f6; }
                    
                    .last-border-0:last-child { border-bottom: none !important; }
                    .ledger-row-clean { transition: background-color 0.2s ease; }
                    .ledger-row-clean:hover { background-color: rgba(0,0,0,0.02); }
                    .target-ledger-row { background-color: #e0f2fe; border-left: 3px solid #0ea5e9; }
                    .compact-journal-card { border: 1px solid #e2e8f0 !important; box-shadow: 0 1px 3px rgba(0,0,0,0.02); transition: all 0.2s ease; }
                    
                    /* Custom Tabs Style */
                    .custom-ledger-tabs .nav-link { 
                        color: #64748b; font-weight: 700; font-size: 0.8rem; text-transform: uppercase; letter-spacing: 0.025em; padding: 0.75rem 1.25rem; border: none !important; transition: all 0.3s ease; position: relative; opacity: 0.7; 
                    }
                    .custom-ledger-tabs .nav-link:hover { opacity: 1; color: #3b82f6; }
                    .custom-ledger-tabs .nav-link.active { color: #3b82f6; opacity: 1; background: transparent !important; }
                    .custom-ledger-tabs .nav-link.active::after { content: ''; position: absolute; bottom: 0; left: 0; width: 100%; height: 3px; background: #3b82f6; border-radius: 3px; }
                    .custom-ledger-tabs .nav-link.disabled { opacity: 0.3; cursor: not-allowed; }
                    .compact-journal-card:hover { border-color: #cbd5e1 !important; transform: translateX(2px); }
                    .amount-badge { font-family: 'Helvetica Neue', Arial, sans-serif; font-weight: 700; }
                    
                    @keyframes slideUp { from { transform: translateY(10px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                    .animate-slide-up { animation: slideUp 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards; }
                `}</style>

                {/* Integration Modals */}
                {showExpenseModal && (
                    <ExpenseModal
                        showModal={showExpenseModal}
                        handleClose={() => setShowExpenseModal(false)}
                        expenseId={selectedExpenseId}
                        expenseData={selectedExpenseData}
                        isEditMode={isEditMode}
                        users={users}
                        fetchUsers={fetchUsers}
                        expenseTypes={expenseTypes}
                        fetchExpenseTypes={fetchExpenseTypes}
                    />
                )}

                {showPreviewModal && selectedInvoice && (
                    <TaxInvoicePreview
                        showModal={showPreviewModal}
                        handleClose={() => {
                            setShowPreviewModal(false);
                            setSelectedQuotation(null);
                        }}
                        createdInvoice={selectedInvoice}
                        quotationData={selectedQuotation}
                        customIntegration={customIntegration}
                        setCustomIntegration={setCustomIntegration}
                        editableDeploymentIntegration={editableDeploymentIntegration}
                        setEditableDeploymentIntegration={setEditableDeploymentIntegration}
                        customIntegrationDescription={customIntegrationDescription}
                        setCustomIntegrationDescription={setCustomIntegrationDescription}
                        onDownloadPDF={handleDownloadTaxInvoicePDF}
                    />
                )}

                <DocPreviewOffcanvas
                    show={showDocOffcanvas}
                    onHide={() => setShowDocOffcanvas(false)}
                    url={docUrl}
                    downloadUrl={downloadUrl}
                    title={docTitle}
                />
            </Offcanvas.Body>
        </Offcanvas >
    );
};

export default LedgerDetailsOffcanvas;
